import NiceModal from "@ebay/nice-modal-react"
import { KeyRound, Loader2, MoreHorizontal, Pause, Play, Share2, Trash2, Users, X } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { useComputers } from "../../computers/context"
import { type PersonWithAccess, listPeople, removePerson, setPersonPaused } from "../../computers/peopleAdmin"
import { projectColorValue } from "../../projectAppearance"
import { useOptionalCodeStore } from "../../store/context"
import { PersonGlyph } from "../computers/PersonGlyph"
import { RevealOnMount } from "./RevealOnMount"
import { AccessSheet } from "./AccessSheet"
import { LinkSheet } from "./LinkSheet"
import { Section } from "./Section"
import { ShareSheet } from "./ShareSheet"
import { levelWord, untilLabel } from "./sharingCopy"

function accessSummary(person: PersonWithAccess, nodeName: string): string {
    if (person.paused) return "Paused"
    if (person.node) return `Everything on ${nodeName}`
    if (person.level === "none" || person.projects.length === 0) return "Linked, nothing shared yet"
    return `${levelWord(person.level)} · ${person.projects.join(", ")} ${untilLabel(person.expiresAt)}`.trim()
}

const ACTION_CLASS = "btn flex h-8 items-center gap-1.5 px-2.5 text-xs font-medium text-base-content hover:bg-base-300/60 disabled:opacity-50"

/**
 * The people who can reach this computer. Each row reads as a sentence:
 * who, then what they can do and where. The row's actions stay folded
 * behind one quiet control and unfold in place, never in a floating menu:
 * Settings is itself a modal, and nothing should float above it.
 */
export function PeopleList({ nodeId, nodeName, order }: { nodeId: string; nodeName: string; order: number }) {
    const computers = useComputers()
    const codeStore = useOptionalCodeStore()
    const [people, setPeople] = useState<PersonWithAccess[]>([])
    const [projects, setProjects] = useState<Array<{ id: string; name: string }>>([])
    const [busy, setBusy] = useState(false)
    const [pendingId, setPendingId] = useState<string | null>(null)
    const [openId, setOpenId] = useState<string | null>(null)
    const [confirmId, setConfirmId] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)
    const inFlight = useRef<Promise<void> | null>(null)

    const refresh = useCallback(async () => {
        if (inFlight.current) return inFlight.current
        const run = (async () => {
            setBusy(true)
            setError(null)
            try {
                const next = await listPeople(computers.backend, nodeId)
                setPeople(next.people)
                setProjects(next.projects)
            } catch (cause) {
                const message = cause instanceof Error ? cause.message : String(cause)
                setError(
                    /not found|not_found|unknown method/i.test(message)
                        ? `${nodeName} is running an older WildLark without sharing. Update and restart it, then come back here.`
                        : message
                )
            } finally {
                setBusy(false)
            }
        })()
        inFlight.current = run
        try {
            await run
        } finally {
            if (inFlight.current === run) inFlight.current = null
        }
    }, [computers.backend, nodeId, nodeName])

    useEffect(() => {
        void refresh()
    }, [refresh])

    const mutate = async (person: PersonWithAccess, run: () => Promise<void>) => {
        setPendingId(person.principalId)
        setError(null)
        try {
            await run()
            await refresh()
        } catch (cause) {
            setError(cause instanceof Error ? cause.message : String(cause))
        } finally {
            setPendingId(null)
        }
    }

    const projectsForSheet = () => {
        const source = computers.scope === nodeId ? codeStore?.repos.repos : computers.overviews[nodeId]?.projects
        const appearanceById = new Map(
            (source ?? []).map((project) => [
                project.id,
                {
                    color: project.color ? projectColorValue(project.color) : undefined,
                    glyph: project.glyph,
                    projectChars: project.projectChars,
                    projectEmoji: project.projectEmoji,
                },
            ])
        )
        return projects.map((project) => ({ ...project, ...appearanceById.get(project.id) }))
    }

    const share = () => {
        void NiceModal.show(ShareSheet, { nodeId, projects: projectsForSheet(), existingPeople: people.map((person) => person.name) }).then(
            () => void refresh()
        )
    }
    const link = () => {
        void NiceModal.show(LinkSheet, { nodeId }).then(() => void refresh())
    }
    const editAccess = (person: PersonWithAccess) => {
        setOpenId(null)
        void NiceModal.show(AccessSheet, { nodeId, principal: person.principal, projects: projectsForSheet() }).then(() => void refresh())
    }
    const toggle = (person: PersonWithAccess) => {
        setConfirmId(null)
        setOpenId((current) => (current === person.principalId ? null : person.principalId))
    }

    return (
        <Section
            label="People"
            order={order}
            description={people.length > 0 ? `Colleagues who can reach ${nodeName}.` : undefined}
            actions={
                <>
                    <button
                        type="button"
                        className="btn flex h-9 items-center gap-1.5 border border-border bg-base-200 px-3 text-xs font-medium text-base-content hover:bg-base-300/60 max-[600px]:flex-1 max-[600px]:justify-center"
                        onClick={link}
                    >
                        <KeyRound className="h-3.5 w-3.5" />
                        Link with a code
                    </button>
                    <button
                        type="button"
                        className="btn flex h-9 items-center gap-1.5 bg-primary px-3 text-xs font-medium text-primary-content hover:bg-primary/90 disabled:opacity-50 max-[600px]:flex-1 max-[600px]:justify-center"
                        onClick={share}
                        disabled={projects.length === 0}
                    >
                        <Share2 className="h-3.5 w-3.5" />
                        Share a project
                    </button>
                </>
            }
        >
            <div className="border border-border bg-base-200">
                {busy && people.length === 0 ? (
                    <div className="flex items-center gap-2 px-4 py-4 text-xs text-muted">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
                    </div>
                ) : people.length === 0 ? (
                    <div className="flex items-center gap-4 px-4 py-5">
                        <div className="flex h-10 w-10 flex-none items-center justify-center bg-base-100 text-muted">
                            <Users className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                            <div className="text-sm text-base-content">Nobody yet</div>
                            <p className="mt-0.5 text-xs leading-5 text-muted">
                                Share a project to invite someone, or link with a code when you're both at your computers.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="divide-y divide-border">
                        {people.map((person) => {
                            const open = openId === person.principalId
                            const confirming = confirmId === person.principalId
                            const pending = pendingId === person.principalId
                            return (
                                <div key={person.principalId} data-person-row={person.principalId}>
                                    <div className="flex items-center gap-3 px-4 py-3">
                                        <PersonGlyph name={person.name} glyph={person.glyph} color={person.color} size="lg" />
                                        <div className="min-w-0 flex-1">
                                            <div className={`truncate text-sm font-medium ${person.paused ? "text-muted" : "text-base-content"}`}>
                                                {person.name}
                                            </div>
                                            <div className="mt-0.5 flex items-center gap-2 text-xs text-muted">
                                                <span
                                                    className={`h-1.5 w-1.5 flex-none ${person.paused ? "bg-warning" : person.level === "none" ? "bg-base-300" : "bg-success"}`}
                                                    aria-hidden="true"
                                                />
                                                <span className="truncate">{accessSummary(person, nodeName)}</span>
                                                {person.devices > 1 && <span className="flex-none">· {person.devices} devices</span>}
                                            </div>
                                        </div>
                                        {pending ? (
                                            <Loader2 className="h-4 w-4 flex-none animate-spin text-muted" />
                                        ) : (
                                            <button
                                                type="button"
                                                className={`btn flex h-8 w-8 flex-none items-center justify-center ${
                                                    open ? "bg-base-300/60 text-base-content" : "text-muted hover:bg-base-300/60 hover:text-base-content"
                                                }`}
                                                aria-label={`Options for ${person.name}`}
                                                aria-expanded={open}
                                                onClick={() => toggle(person)}
                                            >
                                                {open ? <X className="h-4 w-4" /> : <MoreHorizontal className="h-4 w-4" />}
                                            </button>
                                        )}
                                    </div>
                                    {open && !pending && (
                                        <RevealOnMount
                                            className="flex flex-wrap items-center gap-1 border-t border-border bg-base-100 px-3 py-2"
                                            data-person-actions={person.principalId}
                                        >
                                            {confirming ? (
                                                <>
                                                    <span className="px-1.5 text-xs text-muted">
                                                        Remove {person.name} from {nodeName}? Their access ends right away.
                                                    </span>
                                                    <span className="flex-1" />
                                                    <button
                                                        type="button"
                                                        className="btn flex h-8 items-center justify-center bg-error px-3 text-xs font-medium text-error-content"
                                                        onClick={() => {
                                                            setConfirmId(null)
                                                            setOpenId(null)
                                                            void mutate(person, () => removePerson(computers.backend, nodeId, person.principalId))
                                                        }}
                                                    >
                                                        Remove
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn flex h-8 items-center justify-center px-3 text-xs text-muted hover:text-base-content"
                                                        onClick={() => setConfirmId(null)}
                                                    >
                                                        Keep
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <button type="button" className={ACTION_CLASS} onClick={() => editAccess(person)}>
                                                        <KeyRound className="h-3.5 w-3.5" />
                                                        Change access
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={ACTION_CLASS}
                                                        onClick={() =>
                                                            void mutate(person, () =>
                                                                setPersonPaused(computers.backend, nodeId, person.principalId, !person.paused)
                                                            )
                                                        }
                                                    >
                                                        {person.paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
                                                        {person.paused ? "Resume" : "Pause"}
                                                    </button>
                                                    <span className="flex-1" />
                                                    <button
                                                        type="button"
                                                        className="btn flex h-8 items-center gap-1.5 px-2.5 text-xs font-medium text-muted hover:bg-error/10 hover:text-error"
                                                        onClick={() => setConfirmId(person.principalId)}
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                        Remove
                                                    </button>
                                                </>
                                            )}
                                        </RevealOnMount>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>
            {error && (
                <div role="alert" className="mt-3 border border-error/40 bg-error/10 px-3 py-2 text-sm text-error">
                    {error}
                </div>
            )}
        </Section>
    )
}
